import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from 'shared/api/client.js';

// GET /admin/bank-accounts — every row, active and inactive both
// (backend/src/services/bankAccountService.js#listAll). Same "inactive rows
// stay listed, the page dims them" stance as hooks/useAdminUsers.js — an
// inactive bank account is one that no longer prints on the proforma, not one
// that's gone. Unlike users, bank accounts DO have a reactivate path (the
// PATCH endpoint's isActive flag), so an inactive row still offers an action.
const BANK_ACCOUNTS_KEY = ['admin', 'bankAccounts'];

export function useBankAccounts() {
  return useQuery({
    queryKey: BANK_ACCOUNTS_KEY,
    queryFn: () => apiClient.get('/admin/bank-accounts'),
    select: (data) => data.bankAccounts,
  });
}

// POST /admin/bank-accounts — { bankName, accountName, accountNumber,
// branch?, swift?, sortOrder? }. Merge-then-invalidate, same two-step shape
// as useAdminUsers.js#useCreateSalesUser: append for instant feedback, then
// invalidate so the real sort_order-then-id order catches up.
export function useCreateBankAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => apiClient.post('/admin/bank-accounts', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BANK_ACCOUNTS_KEY });
    },
  });
}

// PATCH /admin/bank-accounts/:id — any subset of the create fields plus
// isActive (the soft-disable / reactivate toggle — no hard delete route
// exists for this table, matching the users screen's stance).
export function useUpdateBankAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }) => apiClient.patch(`/admin/bank-accounts/${id}`, patch),
    onSuccess: (data) => {
      queryClient.setQueryData(BANK_ACCOUNTS_KEY, (prev) => {
        if (!prev || !prev.bankAccounts) return prev;
        return {
          ...prev,
          bankAccounts: prev.bankAccounts.map((acc) =>
            acc.id === data.bankAccount.id ? data.bankAccount : acc
          ),
        };
      });
      queryClient.invalidateQueries({ queryKey: BANK_ACCOUNTS_KEY });
    },
  });
}

export default useBankAccounts;
